import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient, SubscriptionClient } from "../sdk";
import { executeWithRetry, DefaultRetryConfigs } from "../sdk/utils/retry";
import { ConnectionState } from "../sdk/types/transport";
import { RawOrder, OrdersResponse, MultiFeedOrdersData } from "../sdk/types/api";
import { PendingOrder, PendingOrdersConfig, transformRawOrderToPendingOrder } from "../sdk/types/data";

/**
 * Pure function to get order ID from an order object
 * @param order - The pending order object
 * @returns The order ID string
 */
function getOrderId(order: PendingOrder): string {
  return order.orderId;
}

/**
 * Hook for managing pending orders data
 * Uses WebSocket as primary source (via user_updates channel) with HTTP fallback
 * Maintains backward compatibility with polling approach
 */
export function usePendingOrders(config: PendingOrdersConfig) {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [orders, setOrders] = useState<PendingOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const isSubscribedRef = useRef<boolean>(false);
  const initialFetchDoneRef = useRef<boolean>(false);
  const previousWalletAddressRef = useRef<string | undefined>(config.walletAddress);
  // Store config in ref to avoid dependency issues in callbacks
  const configRef = useRef(config);
  configRef.current = config;

  /**
   * Fetch orders from API with retry logic
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   * Uses configRef to access latest config without causing dependency loops
   */
  const fetchOrders = useCallback(async () => {
    if (!infoClientRef.current) return;

    const currentConfig = configRef.current;
    // If walletAddress is not provided, we can't fetch orders
    // The API requires an address parameter
    // Don't set error state if address is simply not yet loaded (empty string)
    // Only set error if we've attempted to fetch and it failed
    if (
      !currentConfig.walletAddress ||
      currentConfig.walletAddress.trim() === ""
    ) {
      // Clear error and orders when address is not available
      setError(null);
      setOrders([]);
      setLoading(false);
      return;
    }

    setError(null);

    try {
      const data: OrdersResponse = await executeWithRetry(
        DefaultRetryConfigs.http,
        () => infoClientRef.current!.getOrders(currentConfig.walletAddress!),
      );

      // OrdersResponse structure: { orders: RawOrder[], totalCount: number, timestamp: string | number }
      const ordersArray: RawOrder[] = data.orders || [];

      if (ordersArray.length > 0) {
        // Fetch all orders (no status filtering - component will filter based on view)
        const allOrders = ordersArray.map(transformRawOrderToPendingOrder);
        // Compare with current orders to avoid unnecessary state updates
        setOrders((prev) => {
          if (prev.length !== allOrders.length) {
            return allOrders;
          }
          // Deep comparison - only update if orders actually changed
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(allOrders);
          return prevStr === newStr ? prev : allOrders;
        });
      } else {
        setOrders((prev) => (prev.length === 0 ? prev : []));
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load orders";
      setError(errorMessage);
      console.error("Failed to fetch orders after retries:", err);
    } finally {
      setLoading(false);
    }
  }, []); // Removed config.walletAddress to prevent loops - using configRef instead

  /**
   * Handle order updates from WebSocket (multi-feed orders sub-channel)
   * Updates orders by order ID - merges new/updated orders and removes completed/cancelled ones
   * Receives MultiFeedOrdersData from multi-feed (can be single order or orders array)
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   */
  const handleOrdersUpdate = useCallback(
    (data: MultiFeedOrdersData | RawOrder) => {
      // Handle MultiFeedOrdersData structure
      let ordersToProcess: RawOrder[] = [];
      
      if ('orders' in data && Array.isArray(data.orders)) {
        // Multi-feed format with orders array
        ordersToProcess = data.orders;
      } else if ('order_id' in data) {
        // Single order update (either MultiFeedOrdersData with single order or RawOrder)
        ordersToProcess = [data as RawOrder];
      } else {
        console.warn("Received invalid order data, skipping:", data);
        return;
      }

      // Process each order
      setOrders((prev) => {
        // Create a map of existing orders by ID for efficient lookup
        const ordersMap = new Map<string, PendingOrder>();
        prev.forEach((order) => {
          const orderId = getOrderId(order);
          if (orderId) {
            ordersMap.set(orderId, order);
          }
        });

        // Process each incoming order
        for (const rawOrder of ordersToProcess) {
          // Validate that we have a valid RawOrder with orderId
          if (
            !rawOrder ||
            typeof rawOrder !== "object" ||
            !("orderId" in rawOrder) ||
            typeof rawOrder.orderId !== "string" ||
            rawOrder.orderId.length === 0
          ) {
            console.warn("Received invalid order data, skipping:", rawOrder);
            continue;
          }

          // Transform the RawOrder to PendingOrder
          const incomingOrder = transformRawOrderToPendingOrder(rawOrder);
          const id = getOrderId(incomingOrder);

          if (!id) {
            // Order without ID - log warning and skip
            console.warn("Received order without ID, skipping:", incomingOrder);
            continue;
          }

          // If order has a status that indicates it's no longer active, remove it
          // Note: incomingOrder.status is already normalized (ORDER_STATUS_ prefix removed)
          // by transformRawOrderToPendingOrder, so we check normalized values
          const status = incomingOrder.status.toUpperCase();
          const isInactive =
            status === "FILLED" ||
            status === "CANCELLED" ||
            status === "REJECTED" ||
            status === "EXPIRED" ||
            status === "CLOSED" ||
            status === "DONE";
          // Note: PARTIAL, PENDING, and OPEN are considered active (pending) statuses

          if (isInactive) {
            // Remove completed/cancelled orders
            ordersMap.delete(id);
          } else {
            // Update existing order or add new one
            ordersMap.set(id, incomingOrder);
          }
        }

        // Convert map back to array, filtering out any undefined IDs
        const updatedOrders = Array.from(ordersMap.values()).filter(
          (order) => getOrderId(order) !== undefined,
        );

        // Only update if orders actually changed
        if (prev.length !== updatedOrders.length) {
          return updatedOrders;
        }

        // Deep comparison - only update if orders actually changed
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(updatedOrders);
        return prevStr === newStr ? prev : updatedOrders;
      });
    },
    [],
  );

  // Initialize clients and set up WebSocket
  useEffect(() => {
    // Reset initial fetch flag when wallet address changes
    // This ensures a fresh REST fetch for each new wallet address
    if (config.walletAddress !== previousWalletAddressRef.current) {
      initialFetchDoneRef.current = false;
      previousWalletAddressRef.current = config.walletAddress;
    }

    // Skip initialization if walletAddress is not available
    // This prevents unnecessary client creation and fetch attempts
    if (!config.walletAddress || config.walletAddress.trim() === "") {
      setLoading(false);
      setError(null);
      setOrders([]);
      // Clean up any existing connections
      if (subscriptionClientRef.current) {
        subscriptionClientRef.current.disconnect();
        subscriptionClientRef.current = null;
      }
      initialFetchDoneRef.current = false;
      return;
    }

    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;

    // Initial fetch from REST API only (once per wallet address)
    if (!initialFetchDoneRef.current) {
      fetchOrders().finally(() => {
        initialFetchDoneRef.current = true;
      });
    }

    const subscriptionClient = config.subscriptionClient;

    if (useWebSocket) {
      // WebSocket mode: primary source
      subscriptionClientRef.current = subscriptionClient;

      // Subscribe function that can be called on initial connect or reconnection
      const subscribe = () => {
        // Check if ready for subscriptions (connected and authenticated if required)
        if (!subscriptionClient.isReadyForSubscriptions()) {
          if (subscriptionClient.isConnected() && !subscriptionClient.isReadyForSubscriptions()) {
            // Connected but not authenticated - wait a bit and retry
            const authState = subscriptionClient.getAuthState();
            if (authState === 'pending') {
              console.debug("[usePendingOrders] Waiting for authentication to complete...");
              // Will retry on next state change
            } else if (authState === 'failed') {
              console.error("[usePendingOrders] Authentication failed - cannot subscribe to orders");
              setError("Authentication failed - cannot subscribe to orders");
            }
          }
          return;
        }

        // Check if we need to re-subscribe (marketId changed, or not yet subscribed)
        const marketId = config.marketId ?? "0"; // Default to "0" for user-global
        const symbol = "USDC/USDC"; // Default symbol for user-global
        const needsResubscribe = 
          !isSubscribedRef.current ||
          (config.marketId !== undefined && config.marketId !== marketId);

        if (needsResubscribe) {
          // Unsubscribe from previous subscription if it exists
          if (isSubscribedRef.current && subscriptionClientRef.current) {
            try {
              subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
                "orders",
                handleOrdersUpdate,
              );
            } catch (error) {
              console.warn("[usePendingOrders] Error unsubscribing from previous orders subscription:", error);
            }
          }

          try {
            // Use multi-feed sub-channel for orders (same subscription as other user data)
            subscriptionClient.subscribeMultiFeedSubChannel(
              "orders",
              marketId,
              handleOrdersUpdate,
              { symbol },
            );
            isSubscribedRef.current = true;
            setLoading(false);
            console.debug("[usePendingOrders] Subscribed to orders:", {
              marketId,
              symbol,
            });
          } catch (error) {
            console.error("[usePendingOrders] Failed to subscribe to orders:", error);
            setError(
              error instanceof Error
                ? error.message
                : "Failed to subscribe to orders"
            );
            isSubscribedRef.current = false;
            setLoading(false);
          }
        }
      };

      // Set initial connection state
      setIsConnected(subscriptionClient.isConnected());
      setLoading(false);

      // Try to subscribe immediately if client is ready
      if (subscriptionClient.isReadyForSubscriptions()) {
        subscribe();
      }

      // Monitor connection state changes and re-subscribe on reconnection
      // Use the public onStateChange method from SubscriptionClient
      const unsubscribeStateChange = subscriptionClient.onStateChange(
        (state: ConnectionState) => {
          console.debug("[usePendingOrders] WebSocket state changed to:", state);
          setIsConnected(state === ConnectionState.Connected);
          if (state === ConnectionState.Connected) {
            // Always try to subscribe on connection (subscribe function handles re-subscription logic)
            subscribe();
          } else if (
            state === ConnectionState.Disconnected ||
            state === ConnectionState.Reconnecting
          ) {
            // Don't reset isSubscribedRef here - let subscribe() handle re-subscription
            // This allows us to track if we need to re-subscribe with new params
          }
        }
      );

      return () => {
        // Unsubscribe from state changes
        if (unsubscribeStateChange) {
          unsubscribeStateChange();
        }

        // Unsubscribe from orders sub-channel
        if (subscriptionClientRef.current && isSubscribedRef.current) {
          try {
            subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
              "orders",
              handleOrdersUpdate,
            );
          } catch (error) {
            console.warn("[usePendingOrders] Error unsubscribing during cleanup:", error);
          }
        }

        // Reset subscription state
        isSubscribedRef.current = false;
      };
    } else {
      // If WebSocket is disabled, only do initial REST fetch (no polling)
      // This mode is not recommended - WebSocket should be used for real-time updates
      return () => {
        // Cleanup if needed
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.subscriptionClient,
    config.baseURL,
    config.authToken,
    config.walletAddress,
    config.marketId,
    useWebSocket,
    // Note: fetchOrders and handleOrdersUpdate are intentionally NOT in dependencies
    // fetchOrders uses configRef to access latest config without causing dependency loops
    // handleOrdersUpdate is stable (uses pure function transformRawOrderToPendingOrder) and subscription is managed separately
    // The effect will re-run when walletAddress changes (included above), which is the desired behavior
  ]);

  return {
    orders,
    loading,
    error,
    isConnected,
    refresh: fetchOrders,
  };
}
